import { FormType, type Form } from '../../interfaces/form.interface';
import {
    ReactiveFormHostNavigationState,
    type ReactiveFormHostBackNavigation,
    type ReactiveFormHostResettableRequest,
} from './reactive-form-host-navigation-state';
import {
    ReactiveFormHostRequestButtonState,
    ReactiveFormHostRequestButtonType,
} from './reactive-form-host-request-button-state';
import {
    ReactiveFormHostDashboardTitleBridge,
    type ReactiveFormHostDashboardTitleTarget,
} from './reactive-form-host-dashboard-title-bridge';
import { ReactiveFormHostDestroyController, type ReactiveFormHostDestroyResult } from './reactive-form-host-destroy-controller';
import { ReactiveFormHostDiagnostics, type ReactiveFormHostDiagnosticEntry } from './reactive-form-host-diagnostics';
import { ReactiveFormHostDuplicatePolicy } from './reactive-form-host-duplicate-policy';
import {
    ReactiveFormHostFormSetupCoordinator,
    type ReactiveFormHostFormSetupResult,
    type ReactiveFormHostRequestSource,
} from './reactive-form-host-form-setup-coordinator';
import {
    ReactiveFormHostLifecycle,
    type ReactiveFormHostLifecycleFormBuilder,
} from './reactive-form-host-lifecycle';
import { ReactiveFormHostModeResolver } from './reactive-form-host-mode-resolver';
import {
    ReactiveFormHostLoadCoordinator,
    type ReactiveFormHostLoadCoordinatorReadinessOptions,
} from './reactive-form-host-load-coordinator';
import {
    type ReactiveFormLoadPlan,
    type ReactiveFormLoadPolicy,
    type ReactiveFormLoadReadinessSnapshot,
} from '../load/reactive-form-load-orchestrator';
import { ReactiveFormHostPreviousDataController } from './reactive-form-host-previous-data-controller';
import {
    ReactiveFormHostReadinessCoordinator,
    type ReactiveFormHostReadinessSnapshot,
} from './reactive-form-host-readiness-coordinator';
import {
    ReactiveFormHostStableChangesController,
    type ReactiveFormHostStableChange,
} from './reactive-form-host-stable-changes-controller';
import {
    ReactiveFormHostState,
    type ReactiveFormHostMode,
    type ReactiveFormHostStateInitialValue,
} from './reactive-form-host-state';
import { ReactiveFormHostTitleState } from './reactive-form-host-title-state';
import { ReactiveFormHostUndoState } from './reactive-form-host-undo-state';

export interface ReactiveFormHostControllerOptions<TModel> extends ReactiveFormHostStateInitialValue<TModel> {
    readonly minimumReadyDelayMs?: number | undefined;
}

export interface ReactiveFormHostDuplicateOptions {
    readonly fieldsToDelete?: readonly string[] | undefined;
    readonly idField?: string | undefined;
    readonly mutate?: boolean | undefined;
}

export interface ReactiveFormHostTitleOptions {
    readonly titleWhenEdit: string;
    readonly titleWhenNew: string;
    readonly titleWhenDuplicate: string;
    readonly titleWhenRead?: string | undefined;
}

export interface ReactiveFormHostReadinessOptions {
    readonly requireRepeatables?: boolean | undefined;
    readonly requireMinimumReadyTime?: boolean | undefined;
}

export interface ReactiveFormHostLoadPlanOptions<TModel = unknown> {
    readonly previousData?: Partial<TModel> | null | undefined;
    readonly policy?: ReactiveFormLoadPolicy | undefined;
    readonly markShellReady?: boolean | undefined;
    readonly markRecordReady?: boolean | undefined;
}

export interface ReactiveFormHostLoadReadinessControllerOptions {
    readonly policy?: ReactiveFormLoadPolicy | undefined;
    readonly requireRepeatables?: boolean | undefined;
    readonly requireMinimumReadyTime?: boolean | undefined;
    readonly criticalSupportDataReady?: boolean | undefined;
    readonly deferredCatalogsReady?: boolean | undefined;
    readonly deferredSupportDataReady?: boolean | undefined;
    readonly lazyCatalogsReady?: boolean | undefined;
    readonly hasBlockingErrors?: boolean | undefined;
}

export interface ReactiveFormHostControllerBackNavigationOptions {
    readonly pathToGoBack: string;
    readonly request?: ReactiveFormHostResettableRequest | null | undefined;
    readonly dashboardSegment?: string | undefined;
}

export interface ReactiveFormHostUndoOptions {
    readonly enabledModes?: readonly ReactiveFormHostMode[] | undefined;
    readonly explicitValue?: boolean | undefined;
}

export interface ReactiveFormHostSetupOptions {
    readonly flag: string | null | undefined;
    readonly fieldsToDelete?: readonly string[] | undefined;
    readonly duplicateIdField?: string | undefined;
    readonly duplicateMutate?: boolean | undefined;
    readonly titleWhenEdit: string;
    readonly titleWhenNew: string;
    readonly titleWhenDuplicate: string;
    readonly titleWhenRead?: string | undefined;
    readonly undoEnabledModes?: readonly ReactiveFormHostMode[] | undefined;
    readonly undoEnabled?: boolean | undefined;
    readonly requestButtonFallbackType?: ReactiveFormHostRequestButtonType | undefined;
    readonly form?: Form<unknown> | null | undefined;
    readonly loadPolicy?: ReactiveFormLoadPolicy | undefined;
    readonly markLoadRecordReady?: boolean | undefined;
}

export interface ReactiveFormHostDashboardTitleOptions {
    readonly target: ReactiveFormHostDashboardTitleTarget | null | undefined;
    readonly title: string;
}

/** Public host controller for common create/edit/duplicate and readiness concerns. */
export class ReactiveFormHostController<TModel> {
    public readonly state: ReactiveFormHostState<TModel>;
    public readonly diagnostics = new ReactiveFormHostDiagnostics();

    private readonly minimumReadyDelayMs: number | undefined;

    public constructor(options: ReactiveFormHostControllerOptions<TModel> = {}) {
        this.state = new ReactiveFormHostState<TModel>(options);
        this.minimumReadyDelayMs = options.minimumReadyDelayMs;
    }

    public setMode(mode: ReactiveFormHostMode): void {
        this.state.setMode(mode);
    }

    public setModeFromFlag(flag: string | null | undefined): void {
        this.state.setMode(ReactiveFormHostModeResolver.normalizeMode(flag));
    }

    public setModeFromFormType(formType: FormType): void {
        this.state.setModeFromFormType(formType);
    }

    public resolveFormTypeFromPath(path: string | null | undefined): FormType {
        return ReactiveFormHostModeResolver.resolveFormTypeFromPath(path);
    }

    public resolveModeFromPath(path: string | null | undefined): ReactiveFormHostMode {
        return ReactiveFormHostModeResolver.resolveFromPath(path).mode;
    }

    public resolveModeFromModel(model: TModel | null | undefined, idField?: string | undefined): ReactiveFormHostMode {
        return ReactiveFormHostModeResolver.resolveModeFromModel({ model, idField });
    }

    public resolveModeFromFormType(formType: FormType): ReactiveFormHostMode {
        return ReactiveFormHostModeResolver.resolveModeFromFormType(formType);
    }

    public resolveCurrentTitle(options: ReactiveFormHostTitleOptions): string {
        return ReactiveFormHostTitleState.resolve({
            mode: this.state.mode,
            titleWhenEdit: options.titleWhenEdit,
            titleWhenNew: options.titleWhenNew,
            titleWhenDuplicate: options.titleWhenDuplicate,
            titleWhenRead: options.titleWhenRead,
        });
    }

    public applyDashboardTitle(options: ReactiveFormHostDashboardTitleOptions): string {
        return ReactiveFormHostDashboardTitleBridge.apply(options);
    }

    public setupForm(options: ReactiveFormHostSetupOptions): ReactiveFormHostFormSetupResult<TModel> {
        const setup = ReactiveFormHostFormSetupCoordinator.resolve({
            state: this.state,
            flag: options.flag,
            model: this.state.model,
            fieldsToDelete: options.fieldsToDelete,
            duplicateIdField: options.duplicateIdField,
            duplicateMutate: options.duplicateMutate,
            titles: {
                titleWhenEdit: options.titleWhenEdit,
                titleWhenNew: options.titleWhenNew,
                titleWhenDuplicate: options.titleWhenDuplicate,
                titleWhenRead: options.titleWhenRead,
            },
            undoEnabledModes: options.undoEnabledModes,
            undoEnabled: options.undoEnabled,
            requestButtonFallbackType: options.requestButtonFallbackType,
        });

        if (options.form) {
            this.createLoadPlan(options.form as Form<TModel>, {
                policy: options.loadPolicy,
                markRecordReady: options.markLoadRecordReady ?? true,
            });
        }

        return setup;
    }

    public resolveRequestSource(mode: ReactiveFormHostMode = this.state.mode): ReactiveFormHostRequestSource {
        return ReactiveFormHostFormSetupCoordinator.resolveRequestSource(mode);
    }

    public createLoadPlan(form: Form<TModel>, options: ReactiveFormHostLoadPlanOptions<TModel> = {}): ReactiveFormLoadPlan {
        return ReactiveFormHostLoadCoordinator.createPlan({
            state: this.state,
            form,
            previousData: options.previousData,
            policy: options.policy,
            markShellReady: options.markShellReady,
            markRecordReady: options.markRecordReady,
        });
    }

    public markLoadShellReady(): void {
        this.state.markLoadShellReady();
    }

    public markLoadRecordReady(): void {
        this.state.markLoadRecordReady();
    }

    public setLoadBlockingErrors(hasBlockingErrors: boolean): void {
        this.state.setLoadBlockingErrors(hasBlockingErrors);
    }

    public getLoadReadinessSnapshot(
        options: ReactiveFormHostLoadReadinessControllerOptions = {},
    ): ReactiveFormLoadReadinessSnapshot {
        const coordinatorOptions: ReactiveFormHostLoadCoordinatorReadinessOptions<TModel> = {
            state: this.state,
            policy: options.policy,
            requireRepeatables: options.requireRepeatables,
            requireMinimumReadyTime: options.requireMinimumReadyTime,
            criticalSupportDataReady: options.criticalSupportDataReady,
            deferredCatalogsReady: options.deferredCatalogsReady,
            deferredSupportDataReady: options.deferredSupportDataReady,
            lazyCatalogsReady: options.lazyCatalogsReady,
            hasBlockingErrors: options.hasBlockingErrors,
        };

        return ReactiveFormHostLoadCoordinator.snapshot(coordinatorOptions);
    }

    public applyDuplicatePolicy(model: TModel | null | undefined, options: ReactiveFormHostDuplicateOptions = {}): TModel {
        const duplicatedModel = ReactiveFormHostDuplicatePolicy.apply<TModel>({
            model,
            fieldsToDelete: options.fieldsToDelete,
            idField: options.idField,
            mutate: options.mutate,
        });

        this.state.setModel(duplicatedModel);
        return duplicatedModel;
    }

    public setModel(model: TModel | null | undefined): void {
        this.state.setModel(model);
    }

    public setPreviousData(previousData: TModel | null | undefined): TModel | null {
        return ReactiveFormHostPreviousDataController.setPreviousData({ state: this.state, previousData });
    }

    public setPreviousDataFromModel(): TModel {
        return ReactiveFormHostPreviousDataController.setPreviousDataFromModel(this.state);
    }

    public resolvePreviousData(fallbackModel?: TModel | null | undefined): TModel | null {
        return ReactiveFormHostPreviousDataController.resolvePreviousData({ state: this.state, fallbackModel });
    }

    public setModelChanges(modelChanges: TModel | null | undefined): void {
        this.state.setModelChanges(modelChanges);
    }

    public captureStableValueChange(change: ReactiveFormHostStableChange<TModel> | TModel | null | undefined): TModel {
        return ReactiveFormHostStableChangesController.capture({ state: this.state, change });
    }

    public isPreviousDataChangeEvent(event: { readonly type?: string | null | undefined } | null | undefined): boolean {
        return ReactiveFormHostStableChangesController.isPreviousDataEvent(event);
    }

    public resetExternalDropdowns(formBuilder: ReactiveFormHostLifecycleFormBuilder<TModel> | null | undefined): void {
        ReactiveFormHostLifecycle.resetExternalDropdowns({ state: this.state, formBuilder });
    }

    public finalizeExternalDropdowns(formBuilder: ReactiveFormHostLifecycleFormBuilder<TModel> | null | undefined): void {
        ReactiveFormHostLifecycle.finalizeExternalDropdowns({ state: this.state, formBuilder });
    }

    public scheduleMinimumReadyTime(): void {
        ReactiveFormHostLifecycle.scheduleMinimumReadyTime({ state: this.state, delayMs: this.minimumReadyDelayMs });
    }

    public markFormReady(): void {
        ReactiveFormHostReadinessCoordinator.markFormReady(this.state);
    }

    public resetFormReady(): void {
        ReactiveFormHostReadinessCoordinator.resetFormReady(this.state);
    }

    public markRepeatablesReady(): void {
        ReactiveFormHostReadinessCoordinator.markRepeatablesReady(this.state);
    }

    public resetRepeatablesReady(): void {
        ReactiveFormHostReadinessCoordinator.resetRepeatablesReady(this.state);
    }

    public getReadinessSnapshot(options: ReactiveFormHostReadinessOptions = {}): ReactiveFormHostReadinessSnapshot {
        return ReactiveFormHostReadinessCoordinator.snapshot({
            state: this.state,
            requireRepeatables: options.requireRepeatables,
            requireMinimumReadyTime: options.requireMinimumReadyTime,
        });
    }

    public setNavigationPath(pathToGoBack: string | null | undefined): string {
        return ReactiveFormHostNavigationState.setPath({ state: this.state, pathToGoBack });
    }

    public prepareBackNavigation(options: ReactiveFormHostControllerBackNavigationOptions): ReactiveFormHostBackNavigation {
        return ReactiveFormHostNavigationState.prepareBackNavigation({
            state: this.state,
            pathToGoBack: options.pathToGoBack,
            request: options.request,
            dashboardSegment: options.dashboardSegment,
        });
    }

    public resolveUndoEnabled(options: ReactiveFormHostUndoOptions = {}): boolean {
        return ReactiveFormHostUndoState.resolve({
            state: this.state,
            enabledModes: options.enabledModes,
            explicitValue: options.explicitValue,
        });
    }

    public disableUndo(): boolean {
        return ReactiveFormHostUndoState.disable(this.state);
    }

    public resolveRequestButtonType(
        fallbackType?: ReactiveFormHostRequestButtonType | undefined,
    ): ReactiveFormHostRequestButtonType {
        return ReactiveFormHostRequestButtonState.resolve({ state: this.state, fallbackType });
    }

    public destroyRequestsFromHost(host: object | null | undefined): ReactiveFormHostDestroyResult {
        const result = ReactiveFormHostDestroyController.destroy({ host, diagnostics: this.diagnostics });

        this.markDestroyed();
        return result;
    }

    public destroyRequests(sources: readonly unknown[]): ReactiveFormHostDestroyResult {
        const result = ReactiveFormHostDestroyController.destroy({ sources, diagnostics: this.diagnostics });

        this.markDestroyed();
        return result;
    }

    public getDiagnostics(): readonly ReactiveFormHostDiagnosticEntry[] {
        return this.diagnostics.snapshot();
    }

    public markDestroyed(): void {
        this.state.markDestroyed();
    }

    public get mode(): ReactiveFormHostMode {
        return this.state.mode;
    }
}
